import { BasicFrame } from 'components/organisms';
import { Button, Card, Checkbox, Spacer, Title } from 'components';
import { createTransLinksComponents } from '../../../utils/transLinksComponents';
import { frameConfigBasicAffiliateFlow } from '../../../AppConfig';
import { PHONE } from 'utils/constants';
import { postMessageToApp } from '../../../utils/postMessageToApp';
import { ROUTES } from 'utils/constants/constants_routes';
import { Table } from 'components/molecules/Table/Table';
import { Trans, useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai/index';
import { useCallback, useState } from 'react';
import { useErrorManager } from 'InternalLibrary';
import { useGetClientApplication, usePutApplication } from 'api';
import { useNavigate } from 'react-router-dom';
import classes from 'classnames';
import layout from 'scss/layout.module.scss';
import LegalTermsModal from 'components/atoms/LegalTermsModal/LegalTermsModal';
import RegistrationLoading from '../../RegistrationLoading/RegistrationLoading';
import styles from './RepeatedLoanConfirm.module.scss';
import useIovation, { ioBlackBoxAtom } from '../../../hooks/useIovation';

export default function RepeatedLoanConfirm() {
  const i18Next = useTranslation('affiliatesRepeatedLoanConfirm');
  const navigate = useNavigate();
  const { setError } = useErrorManager();
  const [loading, setLoading] = useState(false);
  const [checkValue, setCheckValue] = useState(true);
  const { fetch: fetchGetApplication } = useGetClientApplication();
  const { fetch: fetchPutApplication } = usePutApplication();
  const [showModal, setShowModal] = useState(false);

  const handleModal = () => setShowModal(!showModal);

  const ioBlackBox = useAtomValue(ioBlackBoxAtom);

  const secondTableItems = [
    {
      label: i18Next.t('bankAccount'),
      value: 'ES58-2100-2489-38-5012682',
    },
    {
      label: '',
      secondaryValue: i18Next.t('bankAccountHelper', { phone: PHONE }),
    },
  ];

  const tableItems = [
    {
      label: i18Next.t('table.row1'),
      value: `2 ${i18Next.t('common:months')}`,
    },
    {
      label: i18Next.t('table.row2'),
      value: `1234.00 ${i18Next.t('common:currencySymbol')}`,
    },
    {
      label: i18Next.t('table.row3'),
      value: `2468.00 ${i18Next.t('common:currencySymbol')}`,
    },
    {
      label: i18Next.t('table.row4'),
      value: `2000.00 ${i18Next.t('common:currencySymbol')}`,
    },
    {
      label: i18Next.t('table.row5'),
      value: `234.00 ${i18Next.t('common:currencySymbol')}`,
    },
    {
      label: i18Next.t('table.row6'),
      isStrong: true,
      value: '02/03/2022',
    },
  ];

  useIovation();

  const onCheckboxHandler = useCallback(
    () => setCheckValue(!checkValue),
    [checkValue],
  );

  const onSubmit = async () => {
    try {
      setLoading(true);

      const response: any = await fetchPutApplication({
        body: {
          ioBlackBox,
        },
      });
      const application = await fetchGetApplication();

      const nextRoute = response.rejected
        ? ROUTES.ACCOUNT_REPEATED_LOAN_REJECTED_APPLICATION
        : ROUTES.ACCOUNT_REPEATED_LOAN_APPROVED_APPLICATION;
      if (!response.rejected) {
        postMessageToApp('loan_first_main', {
          productNumber: application?.productInfo.number,
          amount: application?.amount,
          term: application?.term,
        });
      }
      navigate(nextRoute);
    } catch (error) {
      if (error.status === 400) {
        navigate(ROUTES.ACCOUNT_REPEATED_LOAN_REJECTED_APPLICATION);
      } else {
        setLoading(false);
        setError(error);
      }
    }
  };

  const onConfirmHandler = useCallback(async () => {
    setLoading(true);
    await onSubmit();
    setLoading(false);
  }, [onSubmit, setLoading]);

  const tableItemsWithCustomClass = useCallback(
    () =>
      tableItems.map((item: any) =>
        item.isStrong ? { ...item, className: styles.strong } : item,
      ),
    [tableItems],
  );

  const secondTableItemsWithCustomClass = useCallback(
    () =>
      secondTableItems.map((item: any) => {
        const transformedItem =
          item.name === 'bankAccount'
            ? {
                ...item,
                value: getHiddenBankAccount(
                  item.value ? item.value : 'XXXX-XXXX-XXXX-XX-XXXXXXX',
                ),
                className: styles['bank-account-text'],
              }
            : item;

        return transformedItem.secondaryValue
          ? { ...transformedItem, className: styles['secondary-value'] }
          : transformedItem;
      }),
    [secondTableItems],
  );

  const getHiddenBankAccount = (bankAccount: string) => {
    const charactersToShow = 4;
    const [partToHide, partToShow] = [
      bankAccount.slice(0, bankAccount.length - charactersToShow),
      bankAccount.slice(
        bankAccount.length - charactersToShow,
        bankAccount.length,
      ),
    ];

    const transformedPartToHide = partToHide.replace(/[a-z0-9]/gi, 'X');

    return transformedPartToHide + partToShow;
  };

  if (loading) {
    return <RegistrationLoading />;
  }

  return (
    <BasicFrame
      {...frameConfigBasicAffiliateFlow}
      contentWrapperClassName={layout['width-md']}
    >
      <Title
        className={styles.title}
        text={i18Next.t('title')}
        dataTest="repeatedLoanConfirm-title"
      />
      <Spacer
        marginTop={{ default: '40px', s: '30px', xs: '20px' }}
        width="100%"
      >
        <Card>
          <Spacer padding="25px">
            <Table
              classNameTitle={styles.tableTitle}
              className={styles.table}
              items={tableItemsWithCustomClass()}
            />
          </Spacer>
        </Card>
      </Spacer>
      <Spacer marginTop={{ default: '30px', s: '15px' }}>
        <Card>
          <Spacer padding="25px">
            <Table
              className={styles.table}
              items={secondTableItemsWithCustomClass()}
            />
          </Spacer>
        </Card>
      </Spacer>
      <Spacer marginTop="30px" width="100%">
        <Checkbox
          className={styles.checkbox}
          labelClassName={styles['checkbox-text']}
          text={
            <Trans
              i18nKey="affiliatesRepeatedLoanConfirm:checkTerms"
              components={createTransLinksComponents(handleModal)}
            />
          }
          value={checkValue}
          onChange={onCheckboxHandler}
        />
      </Spacer>

      <Spacer
        className={classes(styles['confirm-btn-wrapper'], layout['width-sm'])}
        marginTop={{ default: '60px', s: '35px' }}
      >
        <Button
          className={classes([styles['confirm-btn'], layout['width-sm']])}
          disabled={!checkValue}
          loading={loading}
          onClick={onConfirmHandler}
        >
          {i18Next.t('confirmBtn')}
        </Button>
      </Spacer>
      {showModal && <LegalTermsModal onClose={handleModal} />}
    </BasicFrame>
  );
}
